import { useEffect } from 'react';
import { useIntl } from 'react-intl';

// Keep the document <title> and the SEO / social meta tags in step with the
// active locale AND the active route. The static index.html carries the pt-BR
// home baseline for crawlers and the no-JS path; these hooks rewrite the same
// tags client-side.
//
// Two layers:
//   • useLocaleMeta()  — called once in __root.tsx. Sets only the
//     locale-dependent, route-independent tag (og:locale), mirroring the
//     `<html lang>` effect. It intentionally does NOT touch title/description
//     so it never races the per-route hook below (child effects run before
//     parent effects in React, so a parent-set title would clobber the route).
//   • useRouteMeta()   — called by each page route with its own message ids.
//     This owns the title + description + social tags for that page.

// Update a meta tag if it already exists (index.html ships the home set),
// otherwise create it so metadata stays complete even if the baseline drifts.
function upsertMeta(attr: 'name' | 'property', key: string, value: string | undefined): void {
  if (!value) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

// Route-independent, locale-dependent metadata. Call once at the app root.
export function useLocaleMeta(messages: Record<string, string>): void {
  useEffect(() => {
    upsertMeta('property', 'og:locale', messages['meta.ogLocale']);
  }, [messages]);
}

// Per-route SEO. `titleId`/`descriptionId` are required; `socialId` defaults to
// the description when a page has no distinct social blurb. Resolving through
// useIntl means the copy re-localizes when the visitor switches languages.
export function useRouteMeta(titleId: string, descriptionId: string, socialId?: string): void {
  const intl = useIntl();
  const title = intl.formatMessage({ id: titleId });
  const description = intl.formatMessage({ id: descriptionId });
  const social = intl.formatMessage({ id: socialId ?? descriptionId });

  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', social);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', social);
  }, [title, description, social]);
}
